"use client"

import { useState } from "react"
import { CalendarIcon } from "lucide-react"
import { Button } from "@/components/ui/button"
import { Calendar } from "@/components/ui/calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover"
import { cn } from "@/lib/utils"

interface DateRange {
  start: Date
  end: Date
}

interface DateRangePickerProps {
  dateRange: DateRange
  onChange: (range: DateRange) => void
}

const PRESETS = [
  { label: "30 Days", days: 30 },
  { label: "90 Days", days: 90 },
  { label: "6 Months", days: 180 },
  { label: "1 Year", days: 365 },
]

export function DateRangePicker({ dateRange, onChange }: DateRangePickerProps) {
  const [isOpen, setIsOpen] = useState(false)

  const formatDateRange = (
    range: DateRange,
    options: { includeStartYear?: boolean; includeEndYear?: boolean } = {}
  ) => {
    const baseOptions: Intl.DateTimeFormatOptions = {
      month: "short",
      day: "numeric",
    }

    const startStr = range.start.toLocaleDateString(
      "en-US",
      options.includeStartYear ? { ...baseOptions, year: "numeric" } : baseOptions
    )
    const endStr = range.end.toLocaleDateString(
      "en-US",
      options.includeEndYear ? { ...baseOptions, year: "numeric" } : baseOptions
    )

    return `${startStr} - ${endStr}`
  }

  const rangeDays = Math.ceil(
    (dateRange.end.getTime() - dateRange.start.getTime()) / (1000 * 60 * 60 * 24)
  )
  const crossesYears = dateRange.start.getFullYear() !== dateRange.end.getFullYear()
  const showYearOnCompact = crossesYears || rangeDays > 90

  const handlePreset = (days: number) => {
    const end = new Date()
    end.setDate(end.getDate() + 14) // Include 2 weeks forecast

    const start = new Date()
    start.setDate(start.getDate() - days)

    onChange({ start, end })
    setIsOpen(false)
  }

  const handleCalendarSelect = (range: { from?: Date; to?: Date } | undefined) => {
    if (range?.from && range?.to) {
      onChange({ start: range.from, end: range.to })
    } else if (range?.from) {
      // Single date selected, use as start with 14-day forecast
      const end = new Date(range.from)
      end.setDate(end.getDate() + 44) // ~6 weeks total
      onChange({ start: range.from, end })
    }
  }

  // Calculate max date (16 days from now - forecast limit)
  const maxDate = new Date()
  maxDate.setDate(maxDate.getDate() + 16)

  // Calculate min date (1 year ago)
  const minDate = new Date()
  minDate.setFullYear(minDate.getFullYear() - 1)

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className={cn(
            "h-9 rounded-full px-3 justify-start text-left font-normal gap-1.5 sm:gap-2 text-xs sm:text-sm",
            !dateRange && "text-muted-foreground"
          )}
        >
          <CalendarIcon className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-gray-500 shrink-0" />
          <span className="sm:hidden whitespace-nowrap">
            {formatDateRange(dateRange, {
              includeStartYear: crossesYears,
              includeEndYear: showYearOnCompact,
            })}
          </span>
          <span className="hidden sm:inline whitespace-nowrap">
            {formatDateRange(dateRange, { includeEndYear: true })}
          </span>
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-fit p-3" align="start" sideOffset={8}>
        <div className="pb-2 mb-2 border-b">
          <p className="text-xs font-medium text-gray-600 mb-2">Quick Select</p>
          <div className="grid grid-cols-4 gap-1.5">
            {PRESETS.map((preset) => (
              <Button
                key={preset.label}
                variant="outline"
                size="sm"
                onClick={() => handlePreset(preset.days)}
                className="text-xs h-7 px-2"
              >
                {preset.label}
              </Button>
            ))}
          </div>
        </div>
        <Calendar
          mode="range"
          selected={{
            from: dateRange.start,
            to: dateRange.end,
          }}
          onSelect={handleCalendarSelect}
          numberOfMonths={1}
          disabled={(date) => date > maxDate || date < minDate}
          className="p-0!"
        />
        <div className="pt-2 text-[10px] text-gray-400 text-center">
          Forecast available for next 16 days
        </div>
      </PopoverContent>
    </Popover>
  )
}
